import { Bugsnag } from "@bugsnag/js"
import { css } from "@emotion/react"
import { Button } from "@planningcenter/doxy-web"
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogLabel,
} from "@reach/alert-dialog"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { useCallback, useContext, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useBugsnagClient } from "source/Bugsnag"
import { formattedCents } from "source/giving/utils/money"
import { Icon } from "source/shared/components"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { useChurchCenterIframe } from "source/shared/hooks/useChurchCenterIframe"
import { useSession } from "source/shared/hooks/useSession"
import {
  ConfirmationTokenCreationError,
  DisplayableConfirmationTokenCreationError,
  DonationFormInvariantError,
  DonationFormSubmissionError,
  RetryableDonationFormInvariantError,
  genericRetryableErrorMessage,
} from "../errors"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import {
  createStripeConfirmationToken,
  sendNondeterministicErrorNotifications,
} from "../queries"
import { clearLocalStorage } from "../stores/local_storage"
import {
  Designation,
  FormSubmissionResult,
  RepeatableFrequency,
  State,
  StripeToken,
  Total,
} from "../types"
import { FREQUENCY_TYPE_TO_LABELS } from "../constants"
import { FormSubmission } from "../utils/FormSubmission"
import { PrimaryButton } from "./display/PrimaryButton"
import { updatePaymentElementApplePayOptions } from "./stripe"
import ReCAPTCHA from "react-google-recaptcha"
import { useInvalidateMyGivingQueries } from "source/giving/profile/hooks/useInvalidateMyGivingQueries"
import useProductAnalyticsPublishEvent from "source/shared/hooks/useProductAnalyticsPublishEvent"
import { isChurchCenterApp } from "source/Layout"

interface FormSubmitProps {
  disabled: boolean
  state: State
  totalCents: Total
  calculatedDesignations: Designation[]
  validateProgress: () => boolean
  desktop: boolean
  memoEligible: boolean
}

type SubmissionState = "idle" | "submitting" | "error"

export function FormSubmit({
  disabled,
  state,
  totalCents,
  calculatedDesignations,
  validateProgress,
  desktop,
  memoEligible,
}: FormSubmitProps) {
  const {
    data: {
      attributes: { email_address: currentPersonEmail },
    },
  } = useSession()
  const {
    current_organization: { id: currentOrganizationId },
  } = useContext(ServerRenderedProps)
  const { dispatch, config } = useDonationFormContext()
  const { inIframe } = useChurchCenterIframe()
  const publishEvent = useProductAnalyticsPublishEvent()
  const isCCA = isChurchCenterApp()
  const navigate = useNavigate()
  const stripe = useStripe()
  const stripeElements = useElements()
  const stripePaymentElement = stripeElements?.getElement("payment")
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>("idle")
  const [errorMessage, setErrorMessage] = useState<string>()
  const bugsnagClient: Bugsnag.Client = useBugsnagClient()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const recaptchaEnabled =
    config.recaptcha_site_key !== null &&
    state.frequency.type === "once" &&
    state.stripePaymentMethod?.type !== "apple_pay"
  const {
    invalidateMyGivingDonationsQueries,
    invalidateMyGivingRecurringDonationsQuery,
  } = useInvalidateMyGivingQueries()

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset()
    }
  }

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      submitDonation({ recaptchaCode: token })
    } else {
      showError(genericRetryableErrorMessage)
    }
  }

  const handleCaptchaError = () => {
    showError(genericRetryableErrorMessage)
  }

  const showError = useCallback((message: string) => {
    setSubmissionState("error")
    setErrorMessage(message)
    resetCaptcha()
  }, [])

  const handleSubmit = () => {
    setErrorMessage(undefined)

    // Need to check for errors now, not wait for revalidation
    const isValid = validateProgress()
    if (!isValid) {
      return
    }

    if (recaptchaEnabled) {
      executeCaptcha()
    } else {
      submitDonation({})
    }
  }

  const executeCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.execute()
    } else {
      const error = new RetryableDonationFormInvariantError()
      showError(error.message)
      bugsnagClient.notify(error)
    }
  }

  const obtainStripeToken = async (): Promise<StripeToken | null> => {
    if (!stripe || !stripeElements) {
      throw new RetryableDonationFormInvariantError()
    } else if (state.stripePaymentMethod?.id) {
      return state.stripePaymentMethod.id
    } else {
      const stripeConfirmationToken = await createStripeConfirmationToken({
        stripe,
        stripeElements,
        // At time of writing, only authenticated users can submit donations, so
        // we are fine asserting this is not null here
        billingEmail: currentPersonEmail!,
        organizationId: currentOrganizationId,
      })
      return stripeConfirmationToken?.id || null
    }
  }

  const submitDonation = async ({
    recaptchaCode,
  }: {
    recaptchaCode?: string
  }) => {
    setSubmissionState("submitting")

    if (stripePaymentElement) {
      updatePaymentElementApplePayOptions(
        stripePaymentElement,
        state.frequency,
        calculatedDesignations,
        config,
        totalCents,
      )
    }

    try {
      const stripeToken = await obtainStripeToken()
      if (!stripeToken) {
        // User cancelled - just reset state
        closeModal()
        return
      }

      const submission = FormSubmission.create({
        state: { ...state, memoEligible },
        calculatedDesignations,
        stripeToken,
        recaptchaCode,
        embedded: inIframe,
      })
      const result = await submission.submit()
      handleSubmitSuccess(result)
      publishEvent("giving.donation-form.submit-successful", {
        source: isCCA ? "CCA" : desktop ? "CCW Desktop" : "CCW Mobile",
      })
    } catch (error) {
      handleSubmitError(error)
    }
  }

  const handleSubmitSuccess = (result: FormSubmissionResult) => {
    clearLocalStorage()

    if (result.recurring) {
      invalidateMyGivingRecurringDonationsQuery()
      navigate("/giving/donation_thank_you", {
        state: {
          recurring: true,
          date: (state.frequency as RepeatableFrequency).starting,
          awaitingVerification: result.awaitingVerification,
        },
      })
    } else {
      invalidateMyGivingDonationsQueries()
      if (result.awaitingVerification) {
        navigate("/giving/donation_thank_you", {
          state: {
            awaitingVerification: true,
          },
        })
      } else if (result.delayed) {
        navigate("/giving/donation_processing", {
          state: { nondeterministicError: false },
        })
      } else {
        dispatch({
          type: "START_POLLING_DONATION_STATUS",
          donationId: result.id,
        })
      }
    }
  }

  const handleSubmitError = (error: Error | unknown) => {
    setSubmissionState("error")

    if (error instanceof DisplayableConfirmationTokenCreationError) {
      showError(error.message)
    } else if (error instanceof ConfirmationTokenCreationError) {
      showError(genericRetryableErrorMessage)
      bugsnagClient.notify(error)
    } else if (error instanceof DonationFormSubmissionError) {
      const { message, path, responseStatus } =
        error as DonationFormSubmissionError
      if (responseStatus == "422" || responseStatus === "429") {
        showError(message)
      } else {
        handleNondeterministicError({ path, responseStatus })
      }
    } else if (error instanceof Error) {
      showError(genericRetryableErrorMessage)
      bugsnagClient.notify(error)
    } else {
      showError(genericRetryableErrorMessage)
      bugsnagClient.notify(
        new DonationFormInvariantError("Unknown donation form submit error"),
      )
    }
  }

  const handleNondeterministicError = async ({
    path,
    responseStatus,
  }: {
    path: string
    responseStatus: string
  }) => {
    try {
      await sendNondeterministicErrorNotifications({ path, responseStatus })
    } catch (error) {
      bugsnagClient.notify(error)
    } finally {
      navigate("/giving/donation_processing", {
        state: { nondeterministicError: true },
      })
    }
  }

  const closeModal = () => {
    setSubmissionState("idle")
    setErrorMessage(undefined)
  }

  return (
    <>
      {recaptchaEnabled && (
        <div className="visually-hidden">
          <ReCAPTCHA
            sitekey={config.recaptcha_site_key!}
            size="invisible"
            ref={recaptchaRef}
            onChange={handleCaptchaChange}
            onError={handleCaptchaError}
          />
        </div>
      )}

      <PrimaryButton
        type="submit"
        onClick={handleSubmit}
        isLoading={submissionState === "submitting"}
        disabled={disabled}
      >
        Give
        {totalCents > 0 && ` ${formattedCents(totalCents)}`}
        {state.frequency.type !== "once" &&
          ` ${FREQUENCY_TYPE_TO_LABELS[state.frequency.type].toLowerCase()}`}
      </PrimaryButton>

      {submissionState !== "idle" && (
        <SubmissionModal
          state={submissionState}
          errorMessage={errorMessage}
          onClose={closeModal}
        />
      )}
    </>
  )
}

function SubmissionModal({
  state,
  errorMessage,
  onClose,
}: {
  state: Omit<SubmissionState, "idle">
  errorMessage?: string
  onClose: () => void
}) {
  const ref = useRef(null)
  const isError = state === "error"

  return (
    <AlertDialog
      leastDestructiveRef={ref}
      onDismiss={isError ? onClose : undefined} // only allow dismissal for errors
    >
      <div ref={ref}>
        {isError ? (
          <ErrorContent message={errorMessage} onClose={onClose} />
        ) : (
          <ProcessingContent />
        )}
      </div>
    </AlertDialog>
  )
}

function ErrorContent({
  message,
  onClose,
}: {
  message?: string
  onClose: () => void
}) {
  return (
    <div className="d-f fd-c ai-c g-3 py-6 pb-3 px-4 ta-c">
      <Icon
        symbol="general#exclamation-triangle"
        className="c-ruby"
        aria-hidden
        css={css`
          width: 3rem;
          height: 3rem;
        `}
      />
      <AlertDialogLabel className="p-0">
        Sorry, something went wrong
      </AlertDialogLabel>
      <AlertDialogDescription className="p-0">{message}</AlertDialogDescription>
      <footer className="d-f jc-c">
        <Button onClick={onClose} text="OK" />
      </footer>
    </div>
  )
}

function ProcessingContent() {
  return (
    <div className="d-f fd-c ai-c g-3 py-8 px-4">
      <div
        css={css`
          width: 4rem;
          height: 4rem;
          display: flex;
          align-items: center;
          justify-content: center;
          --spinner--size: 4rem;
          --spinner--border-width: 0.5rem;
        `}
      >
        <div className="spinner" />
      </div>
      <AlertDialogLabel className="p-0">
        Processing your donation...
      </AlertDialogLabel>
    </div>
  )
}
